import { useEffect, useRef, useState } from 'react';
import { useCartStore } from '@/features/cart/cart.store';
import { startParam } from '@mini/platform/telegram';

const BASE = import.meta.env.VITE_API_URL ?? '';

interface DraftItem {
  product_id: string;
  title: string;
  color: string;
  size: string;
  qty: number;
  unit_price_rub: number;
  print_url: string | null;
  preview_url: string | null;
  print_side?: 'front' | 'back';
  is_custom: boolean;
}

/**
 * Что происходит с корзиной, приехавшей с сайта.
 *
 * `filled` отличается от `done` намеренно: в первом случае человек уже
 * всё выбрал и ему надо показать состав заказа, во втором — он пришёл
 * с пустыми руками, и его место на витрине.
 */
export type DraftState = 'idle' | 'loading' | 'done' | 'filled';

/**
 * Корзина, с которой человек пришёл с сайта.
 *
 * Виджет на сайте заканчивается кнопкой «Перейти»: он откладывает корзину
 * на сервере и уводит человека сюда с ключом в `startapp`. Здесь ключ
 * обменивается на состав — и человек видит ровно то, что собрал, а не
 * пустую витрину с просьбой начать сначала.
 *
 * Корзина не дополняется, а **заменяется**: ключ в ссылке означает
 * «вот этот заказ», и подмешивать к нему вещи из прошлого захода значит
 * положить человеку в счёт то, чего он сейчас не выбирал.
 *
 * Делается ровно один раз за сеанс. Ссылка с ключом остаётся в телеграме
 * навсегда, и повторное применение затирало бы всё, что человек успел
 * собрать после.
 */
export function useIncomingDraft(): DraftState {
  const [state, setState] = useState<DraftState>('idle');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = startParam();
    if (!token) return;

    setState('loading');

    void (async () => {
      try {
        const response = await fetch(`${BASE}/api/v1/drafts/${token}`);

        if (!response.ok) {
          setState('done');
          return;
        }

        const data = (await response.json()) as { items: DraftItem[] };

        if (data.items.length === 0) {
          setState('done');
          return;
        }

        const cart = useCartStore.getState();
        cart.clear();

        for (const item of data.items) {
          cart.add({
            productId: item.product_id,
            title: item.title,
            color: item.color,
            size: item.size,
            qty: item.qty,
            unitPriceRub: item.unit_price_rub,
            printUrl: item.print_url ?? undefined,
            previewUrl: item.preview_url ?? undefined,
            isCustom: item.is_custom,
            printSide: item.print_side ?? 'front',
            placements: [],
          });
        }

        setState('filled');
      } catch {
        // Корзина устарела или сеть подвела. Человек соберёт заново —
        // это неприятно, но лучше пустой витрины с сообщением об ошибке.
        setState('done');
      }
    })();
  }, []);

  return state;
}
